import { useCallback, useEffect, useRef, useState } from 'react';

import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../utils/localStorageUtils';
import { REIMPORT_LOCK_TTL_MS } from '../constants/FeedOverrideConstants';

// A purely client-side, localStorage-persisted "the feed is re-importing your change" lock, keyed per listing.
// It replaces the backend re-import lock table: turning a feed-override section off starts it, it survives a
// refresh, and it drives the syncing banners and the feed-overrides switch's disabled state. Instances stay in
// sync via a window event (same tab) and the storage event (other tabs). It holds no react-query dependency;
// the query invalidation on clear lives in the property-details container instead.
const LOCK_CHANGE_EVENT = 'zrm:reimport-lock-change';

const storageKey = (listingAlias: string) => `zrm-reimport-lock:${listingAlias}`;

const readExpiresAtMs = (listingAlias: string): number => {
  const stored = getLocalStorageItem(storageKey(listingAlias));
  const parsed = stored ? Number(stored) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

// A lock started before the page was left has no timer to clear it once its TTL passes, so a stale expired
// entry can outlive the mount that created it. Detect and remove that entry so the caller can invalidate the
// queries the reimport may have reverted; returns true only when such an expired entry was found and cleared.
export const clearExpiredReimportLock = (listingAlias: string): boolean => {
  const expiresAtMs = readExpiresAtMs(listingAlias);
  if (expiresAtMs > 0 && expiresAtMs <= Date.now()) {
    removeLocalStorageItem(storageKey(listingAlias));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(LOCK_CHANGE_EVENT));
    }
    return true;
  }
  return false;
};

export const useReimportLock = ({ listingAlias }: { listingAlias: string }) => {
  // Read the stored expiry during initial state setup so a refreshed page renders locked immediately instead of
  // flashing unlocked until the mount effect runs. Server-side this is 0 (localStorage access is window-guarded).
  const [expiresAtMs, setExpiresAtMs] = useState(() => readExpiresAtMs(listingAlias));

  // Navigating between listings reuses this component without remounting, so re-read the new listing's stored
  // expiry during render; otherwise the previous listing's lock state carries over until the mount effect runs.
  const renderedAliasRef = useRef(listingAlias);
  if (renderedAliasRef.current !== listingAlias) {
    renderedAliasRef.current = listingAlias;
    setExpiresAtMs(readExpiresAtMs(listingAlias));
  }

  const refresh = useCallback(() => setExpiresAtMs(readExpiresAtMs(listingAlias)), [listingAlias]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(LOCK_CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(LOCK_CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  const isReimportLocked = expiresAtMs > Date.now();

  const startReimportLock = useCallback(() => {
    const nextExpiresAtMs = Date.now() + REIMPORT_LOCK_TTL_MS;
    setLocalStorageItem(storageKey(listingAlias), String(nextExpiresAtMs));
    setExpiresAtMs(nextExpiresAtMs);
    window.dispatchEvent(new Event(LOCK_CHANGE_EVENT));
  }, [listingAlias]);

  // Flip every instance to unlocked once the TTL elapses.
  useEffect(() => {
    if (!isReimportLocked) {
      return;
    }
    const timeoutId = window.setTimeout(
      () => {
        removeLocalStorageItem(storageKey(listingAlias));
        setExpiresAtMs(0);
        window.dispatchEvent(new Event(LOCK_CHANGE_EVENT));
      },
      Math.max(0, expiresAtMs - Date.now()),
    );
    return () => window.clearTimeout(timeoutId);
  }, [isReimportLocked, expiresAtMs, listingAlias]);

  return { isReimportLocked, startReimportLock };
};
